import { useState } from "react";
import { BarChart3, Lock, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";

interface AdminLoginProps {
  onLogin: () => void;
}

export default function AdminLogin({ onLogin }: AdminLoginProps) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const { toast } = useToast();

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    // Simple authentication logic
    setTimeout(() => {
      if (username === "ecotours" && password === "Ecoplace@tours00") {
        toast({
          title: "Login successful",
          description: "Welcome to the admin dashboard",
        });
        onLogin();
      } else {
        setError("Invalid username or password");
        toast({
          variant: "destructive",
          title: "Authentication failed",
          description: "Please check your credentials and try again",
        });
      }
      setIsLoading(false);
    }, 800);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0E1525] p-4">
      <Card className="w-full max-w-md border-[#1C2333] bg-[#1C2333] text-white">
        <CardHeader className="space-y-1 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[#0E1525]">
            <BarChart3 className="h-8 w-8 text-[#3485e4]" />
          </div>
          <CardTitle className="text-2xl font-bold tracking-tight">Admin Dashboard</CardTitle>
          <CardDescription className="text-[#AAAAAA]">
            Enter your credentials to access analytics
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 rounded-md border border-[#ff4c4c30] bg-[#ff4c4c15] p-3 text-[#ff4c4c]">
              {error}
            </div>
          )}
          <form onSubmit={handleLogin}>
            <div className="grid gap-4">
              <div className="relative">
                <User className="absolute left-3 top-3 h-5 w-5 text-[#AAAAAA]" />
                <Input
                  className="border-[#2B3245] bg-[#0E1525] pl-10 text-white placeholder:text-[#666]"
                  placeholder="Username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                />
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-5 w-5 text-[#AAAAAA]" />
                <Input
                  className="border-[#2B3245] bg-[#0E1525] pl-10 text-white placeholder:text-[#666]"
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <Button
                className="bg-[#3485e4] hover:bg-[#2a6dbf] text-white w-full"
                type="submit"
                disabled={isLoading}
              >
                {isLoading ? "Logging in..." : "Login"}
              </Button>
            </div>
          </form>
        </CardContent>
        <CardFooter className="flex justify-center border-t border-[#2B3245] px-6 py-4">
          <p className="text-xs text-[#AAAAAA]">
            Ecoplace Tours Admin Portal © {new Date().getFullYear()}
          </p>
        </CardFooter>
      </Card>
    </div>
  );
}